// Plans (DEV-43). The backend owns the catalog, the effective plan and every
// entitlement check; these hooks only read it for UX (cards, upgrade prompts).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";
import { getToken } from "../../api/token";
import { ADMIN_QUERY_KEY } from "./useAdmin";

export const PLANS_QUERY_KEY = ["plans"];
export const MY_PLAN_QUERY_KEY = ["plans", "me"];

// GET /plans -> { items: Plan[] } — every plan, both audiences.
export const usePlanCatalog = (options = {}) =>
  useQuery({
    queryKey: PLANS_QUERY_KEY,
    queryFn: () => apiFetch("/plans").then((body) => body.items || []),
    staleTime: 10 * 60_000,
    ...options,
  });

// GET /plans/me -> { role, plan, status, source, expires_at, features, limits, legal }.
export const useMyPlan = (options = {}) =>
  useQuery({
    queryKey: MY_PLAN_QUERY_KEY,
    queryFn: () => apiFetch("/plans/me"),
    enabled: (options.enabled ?? true) && Boolean(getToken()),
    staleTime: 5 * 60_000,
    retry: false,
  });

/** True when the session's plan grants `feature`. Unknown (loading/error) →
 *  `fallback`, so a slow /plans/me never hides something the server allows. */
export const useHasFeature = (feature, fallback = true) => {
  const { data } = useMyPlan();
  if (!data) return fallback;
  if (data.status === "admin") return true;
  return Array.isArray(data.features) && data.features.includes(feature);
};

const adminPlanKey = (id) => [...ADMIN_QUERY_KEY, "users", "plan", id];

// GET /admin/users/:id/plan -> { assignment, effective, available }.
export const useAdminUserPlan = (id, options = {}) =>
  useQuery({
    queryKey: adminPlanKey(id),
    queryFn: () => apiFetch(`/admin/users/${id}/plan`),
    enabled: Boolean(id) && (options.enabled ?? true),
  });

const useAdminPlanMutation = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(adminPlanKey(variables.id), data);
      // The user-detail audit list gains an assign/remove row.
      queryClient.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
    },
  });
};

// PUT /admin/users/:id/plan { plan_id, expires_at }.
export const useAssignUserPlan = () =>
  useAdminPlanMutation(({ id, plan_id, expires_at = null }) =>
    apiFetch(`/admin/users/${id}/plan`, { method: "PUT", body: { plan_id, expires_at } }),
  );

// DELETE /admin/users/:id/plan -> back to the role's default (Starter / sin plan).
export const useRemoveUserPlan = () =>
  useAdminPlanMutation(({ id }) => apiFetch(`/admin/users/${id}/plan`, { method: "DELETE" }));
